import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchAllVenues, fetchAllCities, City, Venue } from '../services/api';
import { MapPin } from 'lucide-react';
import { ComingSoonBadge, BARS_COMING_SOON } from './ComingSoon';

const HomePage = () => {
  const [cities, setCities] = useState<City[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);

  useEffect(() => {
    const loadCitiesAndVenues = async () => {
      try {
        const [allCities, allVenues] = await Promise.all([fetchAllCities(), fetchAllVenues()]);
        setVenues(allVenues);
        const citiesWithVenues = allCities.filter(city =>
          allVenues.some(venue => venue.city.toLowerCase() === city.name.toLowerCase())
        );
        setCities(citiesWithVenues);
      } catch (error) {
        console.error('Error loading cities and venues:', error);
      }
    };
    loadCitiesAndVenues();
  }, []);

  // Venues are kept after they lose a star (their earlier ratings are still
  // valid history), so counting every venue in a city overstates the guide -
  // London showed 98 restaurants when the 2026 guide lists 91. Count only
  // venues rated in the most recent year present in the data, which avoids
  // hardcoding a year that would go stale before the next guide lands.
  const latestYear = venues.reduce(
    (max, venue) =>
      venue.yearly_ratings.reduce((m, rating) => Math.max(m, rating.year), max),
    0
  );

  const isCurrentlyRated = (venue: Venue) =>
    venue.yearly_ratings.some(rating => rating.year === latestYear);

  const getVenueCounts = (cityName: string) => {
    const cityVenues = venues.filter(
      venue =>
        venue.city.toLowerCase() === cityName.toLowerCase() && isCurrentlyRated(venue)
    );
    const restaurantCount = cityVenues.filter(venue => venue.venue_type === 'RESTAURANT').length;
    const barCount = cityVenues.filter(venue => venue.venue_type === 'BAR').length;
    return { restaurantCount, barCount };
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-center mb-8 text-gray-800">
        Explore Top Venues by City
      </h1>
      {/* Two cities today, so the grid caps at two columns and the cards are
          given real estate rather than sitting small in a four-up row. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8 max-w-5xl mx-auto">
        {cities.map((city) => {
          const { restaurantCount, barCount } = getVenueCounts(city.name);

          // A city with no currently-rated restaurants has nothing to show, so
          // present it as upcoming rather than as an empty list. Derived from
          // the data rather than naming cities, so it clears itself the moment
          // that city's guide is imported.
          const isComingSoon = restaurantCount === 0;

          const card = (
            <>
              {/* Shorter on phones: at full height two stacked cards no longer
                  fit on screen together, which is the whole point of the grid. */}
              <div className="relative h-44 sm:h-72 lg:h-80 overflow-hidden">
                <img
                  src={city.proxied_image || city.image_url || '/path/to/default-city-image.jpg'}
                  alt={city.name}
                  className={`w-full h-full object-cover transition-transform duration-[1.2s] ease-out
                              group-hover:scale-105 ${isComingSoon ? 'scale-105' : ''}`}
                />
                {/* An upcoming city keeps its colour and gets a richer scrim
                    instead of being greyed out - the point is to make it look
                    worth waiting for, not switched off. */}
                <div
                  className={`absolute inset-0 ${
                    isComingSoon
                      ? 'bg-gradient-to-t from-gray-950/85 via-gray-900/35 to-transparent'
                      : 'bg-gradient-to-t from-black/70 via-black/15 to-transparent'
                  }`}
                />

                {isComingSoon && (
                  <div className="absolute top-3 right-3 sm:top-5 sm:right-5">
                    <ComingSoonBadge />
                  </div>
                )}

                <div className="absolute inset-x-0 bottom-0 p-4 sm:p-6">
                  <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white drop-shadow-sm">
                    {city.name}
                  </h2>

                  {isComingSoon ? (
                    <>
                      {/* Says what is coming, without promising when. The
                          sentence is dropped on phones, where the tags carry
                          the same message in far less height. */}
                      <p className="mt-2 hidden sm:block max-w-sm text-sm font-light leading-relaxed text-white/75">
                        The city's Michelin-starred tables, tracked the same way
                        as London.
                      </p>
                      <div className="mt-2 sm:mt-4 flex flex-wrap gap-1.5 sm:gap-2">
                        {['Michelin stars', 'Live availability', "World's 50 Best"].map((t) => (
                          <span
                            key={t}
                            className="rounded-full border border-white/25 bg-white/10 px-2 sm:px-2.5 py-0.5 sm:py-1
                                       text-[10px] sm:text-[11px] font-light text-white/85 backdrop-blur-sm"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="mt-1.5 sm:mt-2 flex items-center text-white/85">
                      <MapPin size={16} className="mr-1.5" />
                      <span className="text-sm">Explore venues</span>
                    </div>
                  )}
                  {/* Keeps a live card's content block the same height as an
                      upcoming one, so the two titles sit on the same line.
                      Heights differ by breakpoint because the description is
                      hidden on phones. */}
                  {!isComingSoon && (
                    <div className="h-[22px] sm:h-[38px]" aria-hidden="true" />
                  )}
                </div>
              </div>

              <div className="px-4 py-3 sm:px-6 sm:py-5">
                {isComingSoon ? (
                  // Mirrors the live card's stat row so the two line up, with
                  // the numbers held back rather than replaced by a status
                  // claim we cannot stand behind.
                  <div className="flex justify-between items-center text-sm">
                    <div>
                      <span className="font-medium text-gray-300">&mdash;</span>
                      <span className="text-gray-400 ml-1">Top Restaurants</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-300">&mdash;</span>
                      <span className="text-gray-400 ml-1">Top Bars</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between items-center text-sm">
                    <div>
                      <span className="font-medium text-gray-700">{restaurantCount}</span>
                      <span className="text-gray-500 ml-1">
                        Top Restaurant{restaurantCount === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div>
                      {BARS_COMING_SOON ? (
                        <>
                          {/* Bars are real, rated venues in the data - the
                              guide just isn't published yet (VenuePage's
                              Bars tab is a placeholder). A live count here
                              would promise a section that isn't there when
                              you click through. */}
                          <span className="font-medium text-gray-300">&mdash;</span>
                          <span className="text-gray-400 ml-1">Top Bars</span>
                        </>
                      ) : (
                        <>
                          <span className="font-medium text-gray-700">{barCount}</span>
                          <span className="text-gray-500 ml-1">
                            Top Bar{barCount === 1 ? '' : 's'}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          );

          // Not a link while empty - following it would only show "0 found".
          return isComingSoon ? (
            <div
              key={city.id}
              aria-disabled="true"
              className="group bg-white rounded-2xl shadow-sm ring-1 ring-gray-100
                         overflow-hidden cursor-default select-none"
            >
              {card}
            </div>
          ) : (
            <Link
              key={city.id}
              to={`/venues/${city.name}`}
              className="group bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 overflow-hidden
                         transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5"
            >
              {card}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default HomePage;